import "./FrontendReleases.scss";

import React, { useMemo, useState } from "react";

import {
  Alert,
  Button,
  Card,
  Empty,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";

import {
  CheckCircleOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import fileDownload from "js-file-download";
import Papa from "papaparse";

import ConfirmationModal from "../../Components/ConfirmationModal/ConfirmationModal";
import { showAppError } from "../../Utils/error";
import {
  extractHostFromUrl,
  getSingularOrPlural,
  validateHost,
} from "../../Utils/utilityFunctions";
import FrontendReleasesAPI, {
  FRONTEND_RELEASE_ACTIONS,
} from "../api/FrontendReleasesAPI";

const { Text, Title } = Typography;

// Pasting a list is the common case here, so every separator an admin is likely
// to paste with (comma, newline, tab, semicolon, space) splits into a tag.
const TOKEN_SEPARATORS = [",", "\n", "\r", "\t", ";", " "];

const ACTION_LABELS = {
  [FRONTEND_RELEASE_ACTIONS.ENABLE]: "Enable",
  [FRONTEND_RELEASE_ACTIONS.DISABLE]: "Disable",
};

const createBlacklistedHostsCsv = (hosts) =>
  Papa.unparse(
    hosts.map((host) => ({ host })),
    { columns: ["host"], escapeFormulae: true }
  );

/**
 * Hosts get typed, pasted from a sheet or copied from a browser address bar, so
 * accept whatever shape they arrive in and reduce it to a bare hostname.
 * Anything that still is not a valid host is reported back to the caller.
 */
const normalizeHosts = (values = []) => {
  const hosts = [];
  const invalid = [];

  values.forEach((value) => {
    const raw = String(value || "").trim();
    if (!raw) return;

    const host = (extractHostFromUrl(raw) || raw).toLowerCase();

    if (!validateHost(host)) {
      invalid.push(raw);
      return;
    }

    if (!hosts.includes(host)) {
      hosts.push(host);
    }
  });

  return { hosts, invalid };
};

const FrontendReleases = () => {
  const [hosts, setHosts] = useState([]);
  const [action, setAction] = useState(FRONTEND_RELEASE_ACTIONS.ENABLE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const isEnabling = action === FRONTEND_RELEASE_ACTIONS.ENABLE;

  const handleHostsChange = (values) => {
    const { hosts: validHosts, invalid } = normalizeHosts(values);

    if (invalid.length) {
      message.error(
        `Invalid ${getSingularOrPlural(invalid.length, "host")}: ${invalid.join(
          ", "
        )}`
      );
    }

    setHosts(validHosts);
  };

  const handleApply = async () => {
    setIsSubmitting(true);

    try {
      const response = await FrontendReleasesAPI.updateConfiguration({
        hosts,
        action,
      });

      if (!response?.success) {
        showAppError(response, "Failed to update the release configuration");
        return;
      }

      // Report only the hosts the API echoes back - that is the set the change
      // actually landed on. Blacklisted hosts are reported separately by the
      // API so they are never presented as updated.
      const appliedHosts = response?.result?.hosts || [];

      setLastResult({
        hosts: appliedHosts,
        blacklistedHosts: response?.result?.blacklistedHosts || [],
        action: response?.result?.action || action,
      });
      setHosts([]);
      setIsConfirmOpen(false);

      message.success(
        `Version 2 ${
          response?.result?.action === FRONTEND_RELEASE_ACTIONS.DISABLE
            ? "disabled"
            : "enabled"
        } for ${appliedHosts.length} ${getSingularOrPlural(
          appliedHosts.length,
          "host"
        )}`
      );
    } catch (error) {
      showAppError(error, "Failed to update the release configuration");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resultRows = useMemo(
    () =>
      (lastResult?.hosts || []).map((host) => ({
        key: host,
        host,
        action: lastResult.action,
      })),
    [lastResult]
  );

  const blacklistedHosts = lastResult?.blacklistedHosts || [];

  const handleDownloadBlacklistedHosts = () => {
    fileDownload(
      createBlacklistedHostsCsv(blacklistedHosts),
      "frontend-release-failed-hosts.csv"
    );
  };

  const resultColumns = [
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      render: (host) => <Text copyable>{host}</Text>,
    },
    {
      title: "Version 2",
      dataIndex: "action",
      key: "action",
      width: 160,
      render: (value) =>
        value === FRONTEND_RELEASE_ACTIONS.ENABLE ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Enabled
          </Tag>
        ) : (
          <Tag color="error" icon={<StopOutlined />}>
            Disabled
          </Tag>
        ),
    },
  ];

  return (
    <div className="frontend-releases">
      <div className="frontend-releases__header">
        <Title level={3} className="frontend-releases__title">
          Whitelabel V2 Release
        </Title>
        <Text type="secondary">
          Turn the version 2 frontend on or off for whitelabel hosts. The
          selected action is applied to every host in the list.
        </Text>
      </div>

      <Card className="frontend-releases__card" bordered={false}>
        <div className="frontend-releases__field">
          <div className="frontend-releases__field-label">
            <Text strong>Hosts</Text>
            {hosts.length > 0 && (
              <Button
                type="link"
                size="small"
                onClick={() => setHosts([])}
                disabled={isSubmitting}
              >
                Clear all
              </Button>
            )}
          </div>

          <Select
            mode="tags"
            size="large"
            value={hosts}
            onChange={handleHostsChange}
            tokenSeparators={TOKEN_SEPARATORS}
            placeholder="e.g. learn.superaiacademy.com"
            style={{ width: "100%" }}
            disabled={isSubmitting}
            notFoundContent={null}
          />

          <Text type="secondary" className="frontend-releases__hint">
            <InfoCircleOutlined /> Type or paste one or more hosts - a comma,
            space or new line creates a separate host. URLs are trimmed down to
            the host automatically.
          </Text>

          {hosts.length > 0 && (
            <Text className="frontend-releases__count">
              {hosts.length} {getSingularOrPlural(hosts.length, "host")}{" "}
              selected
            </Text>
          )}
        </div>

        <div className="frontend-releases__field">
          <Text strong>Action</Text>
          <Radio.Group
            className="frontend-releases__actions"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            disabled={isSubmitting}
          >
            <Radio value={FRONTEND_RELEASE_ACTIONS.ENABLE}>
              Enable version 2
            </Radio>
            <Radio value={FRONTEND_RELEASE_ACTIONS.DISABLE}>
              Disable version 2
            </Radio>
          </Radio.Group>
        </div>

        <Button
          type="primary"
          size="large"
          danger={!isEnabling}
          loading={isSubmitting}
          disabled={hosts.length === 0}
          onClick={() => setIsConfirmOpen(true)}
        >
          {ACTION_LABELS[action]} version 2
        </Button>
      </Card>

      {lastResult && (
        <Card
          className="frontend-releases__card"
          bordered={false}
          title="Last update"
        >
          <Alert
            type={
              lastResult.action === FRONTEND_RELEASE_ACTIONS.ENABLE
                ? "success"
                : "warning"
            }
            showIcon
            message={`Version 2 ${
              lastResult.action === FRONTEND_RELEASE_ACTIONS.ENABLE
                ? "enabled"
                : "disabled"
            } for ${resultRows.length} ${getSingularOrPlural(
              resultRows.length,
              "host"
            )}`}
            className="frontend-releases__result-alert"
          />

          {blacklistedHosts.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${
                blacklistedHosts.length
              } blacklisted ${getSingularOrPlural(
                blacklistedHosts.length,
                "host"
              )} not updated`}
              description={
                <div className="frontend-releases__blacklisted-hosts">
                  <Text>{blacklistedHosts.join(", ")}</Text>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadBlacklistedHosts}
                  >
                    Download failed hosts CSV
                  </Button>
                </div>
              }
              className="frontend-releases__result-alert"
            />
          )}

          <Table
            columns={resultColumns}
            dataSource={resultRows}
            pagination={false}
            size="small"
            locale={{ emptyText: <Empty description="No hosts updated" /> }}
          />
        </Card>
      )}

      <ConfirmationModal
        open={isConfirmOpen}
        title={`${ACTION_LABELS[action]} version 2?`}
        confirmButtonText={`Yes, ${ACTION_LABELS[action].toLowerCase()}`}
        confirmLoading={isSubmitting}
        onConfirm={handleApply}
        onCancel={() => setIsConfirmOpen(false)}
        customContent={
          <div className="frontend-releases__confirm">
            <Text>
              Version 2 will be {isEnabling ? "enabled" : "disabled"} for the
              following {hosts.length}{" "}
              {getSingularOrPlural(hosts.length, "host")}:
            </Text>
            <Space
              size={[8, 8]}
              wrap
              className="frontend-releases__confirm-hosts"
            >
              {hosts.map((host) => (
                <Tag key={host}>{host}</Tag>
              ))}
            </Space>
          </div>
        }
      />
    </div>
  );
};

export default FrontendReleases;
